import { useState } from 'react';

import {
  SPAWN_AGENTS_TOOL,
  subagentsFor,
  useAgents,
  type ActivityEntry,
  type SubagentRun,
  type SubagentState,
} from '../state/agents.js';

/** Tool chips shown per agent. Older ones fold into a count. */
const TOOL_CHIP_LIMIT = 12;

const STATE_ORDER: readonly SubagentState[] = ['running', 'done', 'failed', 'stopped'];

/** Labels the spawn asked for, shown until the agents report (or after a reload). */
export function requestedLabels(entry: Pick<ActivityEntry, 'arguments'>): string[] {
  const tasks = entry.arguments?.tasks;
  if (!Array.isArray(tasks)) return [];
  return tasks.flatMap((task: unknown) => {
    const label = (task as { label?: unknown } | null)?.label;
    return typeof label === 'string' && label.trim() ? [label.trim()] : [];
  });
}

/** What an agent shows: its final text once it ends, else its latest reply so far. */
export function subagentText(run: SubagentRun): string {
  if (run.summary) return run.summary;
  for (let index = run.lines.length - 1; index >= 0; index -= 1) {
    const line = run.lines[index]!;
    if (line.kind !== 'tool' && line.stream !== 'thinking' && line.text.trim()) return line.text;
  }
  return '';
}

function headline(agents: readonly SubagentRun[], requested: number): string {
  if (agents.length === 0) return requested > 0 ? `${requested} requested` : 'starting';
  const counts = STATE_ORDER.flatMap((state) => {
    const count = agents.filter((run) => run.state === state).length;
    return count > 0 ? [`${count} ${state}`] : [];
  });
  return counts.join(' · ');
}

/**
 * The sub-agents a tool call started, nested under that call's bubble.
 *
 * spawn_agents always gets the panel (its requested labels show until the
 * agents report). Any other tool call shows it only once tagged activity
 * names it as the parent, e.g. a Claude Code workflow agent.
 */
export function SubagentPanel({ entry }: { entry: ActivityEntry }) {
  const group = useAgents((state) => subagentsFor(state, entry));
  const [open, setOpen] = useState(true);
  if (!group && entry.tool !== SPAWN_AGENTS_TOOL) return null;

  const agents = group?.agents ?? [];
  const requested = requestedLabels(entry);
  const finished = entry.ok !== null && entry.ok !== undefined;
  return (
    <details
      className="subagents"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        <span className="subagents-title">Sub-agents</span>
        <span className="subagents-count">{headline(agents, requested.length)}</span>
      </summary>
      {agents.length > 0 && (
        <ol className="subagent-list">
          {agents.map((run) => (
            <SubagentRow key={run.id} run={run} />
          ))}
        </ol>
      )}
      {agents.length === 0 && requested.length > 0 && (
        <ol className="subagent-list">
          {requested.map((label, index) => (
            <li key={`${index}:${label}`} className="subagent is-pending">
              <header>
                <span className="subagent-dot" aria-hidden="true" />
                <strong className="subagent-label">{label}</strong>
                <span className="subagent-state">{finished ? 'ended' : 'starting'}</span>
              </header>
            </li>
          ))}
        </ol>
      )}
      {agents.length === 0 && finished && (
        <p className="muted subagents-note">
          Live sub-agent activity is not kept after a reload. Inspect this tool call for each
          agent&apos;s result.
        </p>
      )}
    </details>
  );
}

function SubagentRow({ run }: { run: SubagentRun }) {
  const tools = run.lines.filter((line) => line.kind === 'tool');
  const shown = tools.slice(-TOOL_CHIP_LIMIT);
  const hidden = tools.length - shown.length;
  const text = subagentText(run);
  return (
    <li className={`subagent is-${run.state}`}>
      <header>
        <span className="subagent-dot" aria-hidden="true" />
        <strong className="subagent-label">{run.label}</strong>
        <span className="subagent-state">{run.state}</span>
        {tools.length > 0 && (
          <span className="muted subagent-meta">
            {tools.length} tool{tools.length === 1 ? '' : 's'}
          </span>
        )}
      </header>
      {shown.length > 0 && (
        <div className="subagent-tools">
          {hidden > 0 && <span className="subagent-tool is-more">+{hidden}</span>}
          {shown.map((line) => (
            <span
              key={line.id}
              className={`subagent-tool${
                line.ok === false ? ' is-bad' : line.ok == null ? ' is-pending' : ''
              }`}
              title={line.text}
            >
              {line.tool ?? 'tool'}
            </span>
          ))}
        </div>
      )}
      {text && <p className="subagent-text">{text}</p>}
    </li>
  );
}
